import React, { useEffect } from 'react'
import { useFormikContext } from 'formik'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { _ } from 'utils'
import { UpdatePresentationSpaceInput } from 'generated/graphql'
import { CardHeader, Divider } from 'components'
import ConfirmModal from 'components/ConfirmModal'
import { addModal } from 'features/modals/modalSlice'
import TextField from '../../forms/components/TextField'
import Switch from '../../forms/components/Switch'
import FileUpload, { IMAGE_ONLY } from '../../forms/components/FileUpload'
import { getChangedKeys } from '../../forms/utils'

const S = {
    Header: styled(CardHeader)`
        margin-left: 10px;
        margin-bottom: 20px;
    `,
    Switch: styled(Switch)`
        margin-bottom: 20px;
    `
}

const Settings = () => {
    const dispatch = useDispatch()
    const { values, setFieldValue, initialValues } = useFormikContext<UpdatePresentationSpaceInput>()
    const changedFields = getChangedKeys(initialValues, values)

    // This effect watches the form. When streamIsContinues is set to false and streamUrl is filled,
    // a modal will be displayed to confirm. If the user:
    // - confirmed: set streamIsContinues to false
    // - canceled: set streamIsContinues back to true
    const isShowConfirmModal= changedFields.includes('streamIsContinues')
        && !values.streamIsContinues
        && values.streamUrl
    useEffect(() => {
        if (isShowConfirmModal) {
            dispatch(addModal({
                Component: ({ close }: { close: () => void }) => (
                    <ConfirmModal
                        close={close}
                        title={_('presentationsettings.confirm.stream_is_continues.title')}
                        confirmDescription={_('presentationsettings.confirm.stream_is_continues.description')}
                        onCancel={() => { setFieldValue('streamIsContinues', true) }}
                    />
                ),
                shouldNotWrap: true
            }))
        }
    }, [dispatch, isShowConfirmModal, setFieldValue])

    const isShowConfirmModalForInteraction = changedFields.includes('interactionIsContinues')
        && !values.interactionIsContinues
        && values.interactionUrl
    useEffect(() => {
        if (isShowConfirmModalForInteraction) {
            dispatch(addModal({
                Component: ({ close }: { close: () => void }) => (
                    <ConfirmModal
                        close={close}
                        title={_('presentationsettings.confirm.interaction_is_continues.title')}
                        confirmDescription={
                            _('presentationsettings.confirm.interaction_is_continues.description')
                        }
                        onCancel={() => { setFieldValue('interactionIsContinues', true) }}
                    />
                ),
                shouldNotWrap: true
            }))
        }
    }, [dispatch, isShowConfirmModalForInteraction, setFieldValue])

    const showChangedConfirmModal = (field: string) => dispatch(addModal({
        Component: ({ close }: { close: () => void }) => (
            <ConfirmModal
                close={close}
                title={_('presentationsettings.confirm.also_change_programs.title')}
                confirmDescription={_('presentationsettings.confirm.also_change_programs.description')}
                onConfirm={() => setFieldValue(`change${field}`, true)}
                onCancel={() => setFieldValue(`change${field}`, false)}
                cancelText={_('button.no')}
                confirmText={_('button.yes')}
                variantConfirm="success"
            />
        ),
        shouldNotWrap: true
    }))

    return (
        <>
            <TextField
                name="parent.name"
                isRequired
                label={_('presentationsettings.form.space_name')}
                lg={1/2}
            />
            <Switch
                name="streamIsContinues"
                label={_('presentationsettings.form.stream_is_continues')}
                helperText={_('presentationsettings.form.stream_url.helper_text')}
            />
            <TextField
                name="streamUrl"
                label={_('presentationsettings.form.stream_url')}
                showInfoIcon
                isDisabled={!values.streamIsContinues}
                lg={1/2}
            />
            <Switch
                name="interactionIsContinues"
                label={_('presentationsettings.form.interaction_is_continues')}
                helperText={_('presentationsettings.form.interaction_url.helper_text')}
            />
            <TextField
                name="interactionUrl"
                label={_('presentationsettings.form.interaction_url')}
                isDisabled={!values.interactionIsContinues}
                showInfoIcon
                lg={1/2}
            />
            <Divider extend={10} marginBottom={10} />
            <S.Header>
                {_('presentationsettings.form.sidebar_header')}
            </S.Header>
            <S.Switch
                name="showAttendees"
                label={_('presentationsettings.form.show_attendees')}
                onClick={() => showChangedConfirmModal('ShowAttendees')}
            />
            <S.Switch
                name="parent.enableChat"
                label={_('presentationsettings.form.enable_chat')}
                onClick={() => showChangedConfirmModal('EnableChat')}
            />
            <Divider extend={10} marginTop={20} />
            <FileUpload
                name="parent.image"
                label={_('presentationsettings.form.background_image')}
                placeholder={_('presentationsettings.form.background_image_placeholder')}
                accept={IMAGE_ONLY}
                previewImagePlacement="top-end"
            />
        </>
    )
}

export default Settings
